import React, { useState } from "react";
import useAuth from "../hooks/useAuth.js";
import { Link } from "react-router-dom";
import api from "../api.js";
import Navbar from "./Navbar.js";
import toast from "react-hot-toast";
import Footer from "./Footer.js";
import contactIllustration from "../assets/images/hero.png";

const ContactUs = () => {
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/contact", formData);
      toast.success(res.data.message || "Message sent!");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "General Inquiry",
        message: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit message");
    }
  };
  return (
    <div className="bg-white">
      <Navbar isAuthenticated={isAuthenticated} />
      {/* Hero Section */}
      <div className="relative bg-gray-700">
        <div className="absolute inset-0 page-header opacity-20"></div>
        <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl font-medium tracking-tight text-white sm:text-5xl lg:text-6xl">
            Contact Us
          </h1>
          <p className="mt-6 text-xl text-gray-100 max-w-3xl mx-auto">
            We're here to help and answer any questions you might have about Ajo
            App.
          </p>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div className="mt-12 lg:mt-0">
              <h2 className="text-3xl font-medium text-gray-900 sm:text-2xl">
                Get in touch
              </h2>
              <p className="mt-4 text-lg text-gray-500">
                Have questions about our platform? Fill out the form and our
                team will get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8 gap-2">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      First name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Last name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8 gap-2">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="mt-1 block w-full border rounded-md py-2 px-3"
                  >
                    <option>General Inquiry</option>
                    <option>Account Support</option>
                    <option>Technical Support</option>
                    <option>Partnership</option>
                    <option>Other</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border rounded-md py-2 px-3"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    className="w-full bg-gray-600 text-white py-3 px-6 rounded-md hover:bg-gray-700"
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
            <div className="relative lg:ml-8">
              <img
                className="relative rounded-lg shadow-xl"
                src={contactIllustration}
                alt="Contact illustration"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact Info Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-3xl font-medium text-gray-900 sm:text-2xl">
              Get in Touch with Us
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-gray-500 rounded-md p-3">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">Email</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      <a
                        href="mailto:support@ajoapp.com"
                        className="text-gray-600 hover:text-gray-500"
                      >
                        support@ajoapp.com
                      </a>
                      <br />
                      <a
                        href="mailto:info@ajoapp.com"
                        className="text-gray-600 hover:text-gray-500"
                      >
                        info@ajoapp.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-gray-500 rounded-md p-3">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">Phone</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      <a
                        href="tel:+1234567890"
                        className="text-gray-600 hover:text-gray-500"
                      >
                        +1 (226) 260-7304
                      </a>
                      <br />
                      <a
                        href="tel:+2348061127296"
                        className="text-gray-600 hover:text-gray-500"
                      >
                        +234 806 112 7296, +234 806 852 1406
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 bg-gray-500 rounded-md p-3">
                    <svg
                      className="h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      Corporate Offices
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      668 Mill Street, Windsor, ON, Canada
                      <br />
                      22 IKB Road, Sector F, FHA Lugbe FCT, Abuja Nigeria
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ CTA Section */}
      <div className="bg-gray-700">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-medium text-white sm:text-2xl">
            <span className="block">Have questions?</span>
            <span className="block">Check our FAQ first.</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-gray-200">
            Many common questions are answered in our frequently asked
            questions.
          </p>
          <Link
            to="/faqs"
            className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-gray-600 bg-white hover:bg-gray-50 sm:w-auto"
          >
            Visit FAQ
          </Link>
        </div>
      </div>
      <Footer />
</div>
  );
};

export default ContactUs;
